import React, { useState, useReducer } from 'react';
import { makeStyles, Typography, Box } from '@material-ui/core';
import * as yup from 'yup';
import { ErrorDiv, EmailInput } from '../utilities/commonUtilities/FormElements';
import NotLoggedInLayout from '../utilities/layouts/NotLoggedInLayout';
import { styleBreakMobile } from '../utilities/layouts/Globals';
import NoLinkAppBar from '../utilities/NavBar/NoLinkAppBar';
import AdaptiveNavBar from '../utilities/NavBar/AdaptiveNavBar';
import { COLOR_WHITE } from '../utilities/MaterialWatchman';
import { PATH_LOGIN } from '../../containers/RoutePaths';
import { ReactComponent as MailBadge } from '../../resources/images/mail-badge.svg';
import { ReactComponent as MailBox } from '../../resources/images/mail-box.svg';
import { ReactComponent as Key } from '../../resources/images/key.svg';
import { FullScreenButtonPage } from '../FullScreenButtonPage';
import { FullScreenInputButtonPage } from '../FullScreenInputButtonPage';
import clsx from 'clsx';

const isWatchman = process.env.REACT_APP_THEME_ENV === 'watchman';

const useStyles = makeStyles(theme => ({
    bodyBackground: {
        height: '100vh',
        background: COLOR_WHITE,
    },
    noNavBarDiv: {
        [styleBreakMobile(theme)]: {
            paddingTop: theme.spacing(12) - 3,
            overflowY: 'hidden',
        },
    },
    contentTitle: {
        marginBottom: theme.spacing(1),
    },
    emailText: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(1),
        [styleBreakMobile(theme)]: {
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(0.5),
        },
    },
    emailInput: {
        width: '380px',
        [styleBreakMobile(theme)]: {
            width: '100%',
        },
    },
    checkEmailBody: {
        maxWidth: theme.spacing(37),
        textAlign: 'center',
        marginTop: theme.spacing(1),
    },
    mailBadge: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(4),
        [styleBreakMobile(theme)]: {
            marginTop: theme.spacing(6),
        },
    },
    largeText: {
        fontSize: theme.spacing(5),
        lineHeight: '55px',
        fontWeight: 700,
        fontStyle: 'normal',
        [styleBreakMobile(theme)]: {
            fontSize: theme.spacing(4),
            textAlign: 'center',
            lineHeight: '41px',
        },
    },
    subtitleText: {
        letterSpacing: '0.3px',
    },
    subtitleContrastText: {
        color: theme.palette.secondary.contrastText,
    },
}));

export default function RequestPasswordReset({ onContinue, history }) {
    const [email, setEmail] = useState('');
    const [pageToDisplay, setIsRequestSucessful] = useReducer((state, action) => {
        switch (action.type) {
            case 'success':
                return 'CheckEmail';
            default:
                return 'TroubleLogging';
        }
    }, 'TroubleLogging');

    switch (pageToDisplay) {
        case 'TroubleLogging':
            return (
                <TroubleLogging
                    onContinue={email => {
                        onContinue(email, setIsRequestSucessful);
                    }}
                    setEmail={setEmail}
                />
            );
        case 'CheckEmail':
            return <CheckEmail email={email} history={history} />;
        default:
            return null;
    }
}

const INVALID_EMAIL_MESSAGE = 'Please enter a valid email.';

const emailSchema = yup.object().shape({
    email: yup.string().email().required(),
});

const troubleLoggingSubtitleText = isWatchman
    ? () => {
          const classes = useStyles();
          return (
              <>
                  <Typography className={clsx(classes.largeText, classes.subtitleContrastText)}>
                      Forgot
                      <Typography className={clsx(classes.largeText, classes.subtitleText)} component="span">
                          {' '}
                          Your Password?
                      </Typography>
                  </Typography>
              </>
          );
      }
    : 'Forgot Password?';
const troubleLoggingBodyText = `To reset your password, please enter the email address you used to register with ${
    isWatchman ? 'Watchman.' : 'Ned Virtual Clinic.'
}`;

const TroubleLogging = ({ onContinue, setEmail }) => {
    const classes = useStyles();
    const [localEmail, setLocalEmail] = useState('');
    const [emailValid, setEmailValid] = useState(false);
    const [emailFocusLost, setEmailFocusLost] = useState(false);

    const onButtonClick = () => {
        onContinue(localEmail);
        setEmail(localEmail);
    };

    const checkEmailValidity = email => {
        // check validity
        emailSchema
            .isValid({
                email: email,
            })
            .then(function (valid) {
                setEmailValid(valid);
            });
    };

    const emailInputField = (
        <>
            <Typography variant="body2" component="div" className={classes.emailText}>
                Email address
            </Typography>
            <EmailInput
                name="email"
                placeholder="Enter email address"
                value={localEmail}
                onChange={e => {
                    setLocalEmail(e.target.value);
                    checkEmailValidity(e.target.value);
                }}
                error={emailFocusLost && localEmail.length > 0 && !emailValid}
                className={classes.emailInput}
                onFocus={() => setEmailFocusLost(false)}
                onBlur={() => setEmailFocusLost(true)}
                adornStart={isWatchman}
                AlternateAdornIcon={isWatchman}
            />
            {emailFocusLost && localEmail.length > 0 && !emailValid && (
                <div>
                    <ErrorDiv text={INVALID_EMAIL_MESSAGE} />
                </div>
            )}
        </>
    );

    const content = (
        <div>
            <Typography variant="h1" component="div" className={classes.contentTitle}>
                {troubleLoggingSubtitleText}
            </Typography>
            <Typography variant="body1" component="div">
                {troubleLoggingBodyText}
            </Typography>
            {emailInputField}
        </div>
    );

    const buttonText = 'Send Email Link';

    return isWatchman ? (
        <div className={classes.bodyBackground}>
            <NoLinkAppBar />
            <FullScreenInputButtonPage
                Icon={Key}
                SubtitleText={troubleLoggingSubtitleText}
                bodyText={troubleLoggingBodyText}
                buttonText={buttonText}
                onButtonClick={onButtonClick}
                buttonDisabled={!emailValid}
                showShadow={isWatchman}
                hideNavbar={isWatchman}
            >
                {emailInputField}
            </FullScreenInputButtonPage>
        </div>
    ) : (
        <NotLoggedInLayout onClick={onButtonClick} buttonDisabled={!emailValid} buttonText={buttonText} content={content} />
    );
};

const checkEmailBody = 'Please check your email for instructions on how to reset your password.';
const checkEmailButtonText = 'Back to Sign In';
const checkEmailSubtitleText = isWatchman
    ? () => {
          const classes = useStyles();
          return (
              <>
                  <Typography className={clsx(classes.largeText, classes.subtitleText)}>
                      Check Your
                      <Typography className={clsx(classes.largeText, classes.subtitleContrastText)} component="span">
                          {' '}
                          Email
                      </Typography>
                  </Typography>
              </>
          );
      }
    : 'Check Your Email';

const CheckEmail = ({ history }) => {
    const classes = useStyles();

    const onButtonClick = () => {
        history.push(PATH_LOGIN);
    };

    const content = (
        <Box display="flex" flexDirection="column" alignItems="center">
            <Box className={classes.mailBadge}>
                <MailBadge aria-labelledby="Mail" />
            </Box>
            <Typography variant="h2" component="div">
                {checkEmailSubtitleText}
            </Typography>
            <Typography variant="body1" component="div" className={classes.checkEmailBody}>
                {checkEmailBody}
            </Typography>
        </Box>
    );

    return isWatchman ? (
        <div className={clsx(classes.bodyBackground, classes.noNavBarDiv)}>
            <AdaptiveNavBar links={[]} />
            <FullScreenButtonPage
                Icon={MailBox}
                SubtitleText={checkEmailSubtitleText}
                bodyText={checkEmailBody}
                buttonText={checkEmailButtonText}
                onButtonClick={() => {
                    history.push(PATH_LOGIN);
                }}
                hideNavbar={isWatchman}
            />
        </div>
    ) : (
        <NotLoggedInLayout onClick={onButtonClick} buttonText={checkEmailButtonText} content={content} contentHorizontallyCentered={true} />
    );
};
